import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Check, X } from 'lucide-react';
import { InlineEditProps } from '../../../types/datagrid';
import DatePicker from '../../forms/DatePicker';

interface InlineEditorImplProps extends Omit<InlineEditProps, 'isLoading' | 'error'> {
  isLoading?: boolean;
  error?: string;
}

export const InlineEditor: React.FC<InlineEditorImplProps> = ({
  value: initialValue,
  column,
  onSave,
  onCancel,
  isLoading = false,
  error,
}) => {
  const [value, setValue] = useState(initialValue);
  const [showError, setShowError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const selectRef = useRef<HTMLSelectElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    // Focus the appropriate input when editor opens
    const timeout = setTimeout(() => {
      if (column.type === 'select' && selectRef.current) {
        selectRef.current.focus();
      } else if (column.type === 'text' && column.key === 'description' && textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.select();
      } else if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, 10);

    return () => clearTimeout(timeout);
  }, [column.type, column.key]);

  useEffect(() => {
    if (error) {
      setShowError(true);
      const timeout = setTimeout(() => setShowError(false), 3000);
      return () => clearTimeout(timeout);
    }
  }, [error]);

  const handleSave = async () => {
    try {
      // Validate value
      if (column.validation?.required && (!value || value === '')) {
        throw new Error(column.validation.message || 'This field is required');
      }

      if (column.validation?.min !== undefined && Number(value) < column.validation.min) {
        throw new Error(
          column.validation.message || `Value must be at least ${column.validation.min}`
        );
      }

      if (column.validation?.max !== undefined && Number(value) > column.validation.max) {
        throw new Error(
          column.validation.message || `Value must be at most ${column.validation.max}`
        );
      }

      if (column.validation?.pattern && !column.validation.pattern.test(String(value))) {
        throw new Error(column.validation.message || 'Invalid format');
      }

      await onSave(value);
    } catch {
      // Error would be handled by parent component
    }
  };

  const handleCancel = () => {
    setValue(initialValue);
    onCancel();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const renderEditor = () => {
    switch (column.type) {
      case 'text': {
        if (
          column.key === 'description' ||
          (typeof initialValue === 'string' && initialValue.length > 100)
        ) {
          return (
            <textarea
              ref={textareaRef}
              value={value || ''}
              onChange={e => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full px-2 py-1 text-sm border border-brand-500 rounded focus:outline-none focus:ring-1 focus:ring-brand-500 resize-none"
              rows={3}
              disabled={isLoading}
            />
          );
        }
        return (
          <input
            ref={inputRef}
            type="text"
            value={value || ''}
            onChange={e => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full px-2 py-1 text-sm border border-brand-500 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
            disabled={isLoading}
          />
        );
      }

      case 'number': {
        return (
          <input
            ref={inputRef}
            type="number"
            value={value || ''}
            onChange={e => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            min={column.validation?.min}
            max={column.validation?.max}
            className="w-full px-2 py-1 text-sm border border-brand-500 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
            disabled={isLoading}
          />
        );
      }

      case 'currency': {
        return (
          <input
            ref={inputRef}
            type="number"
            value={value || ''}
            onChange={e => setValue(parseFloat(e.target.value) || 0)}
            onKeyDown={handleKeyDown}
            step="0.01"
            min={column.validation?.min}
            max={column.validation?.max}
            className="w-full px-2 py-1 text-sm border border-brand-500 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
            disabled={isLoading}
          />
        );
      }

      case 'date': {
        const dateValue = value ? new Date(value).toISOString().split('T')[0] : null;
        return (
          <DatePicker
            value={dateValue}
            onChange={date => setValue(date ?? '')}
            disabled={isLoading}
            className="w-full"
          />
        );
      }

      case 'datetime': {
        const datetimeValue = value ? new Date(value).toISOString() : '';
        return (
          <DatePicker
            value={datetimeValue || null}
            showTimePicker={true}
            onChange={date => setValue(date ?? '')}
            disabled={isLoading}
            className="w-full"
          />
        );
      }

      case 'boolean': {
        return (
          <select
            ref={selectRef}
            value={value ? 'true' : 'false'}
            onChange={e => setValue(e.target.value === 'true')}
            onKeyDown={handleKeyDown}
            className="w-full px-2 py-1 text-sm border border-brand-500 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
            disabled={isLoading}
          >
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        );
      }

      case 'select': {
        return (
          <select
            ref={selectRef}
            value={value || ''}
            onChange={e => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full px-2 py-1 text-sm border border-brand-500 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
            disabled={isLoading}
          >
            <option value="">Select...</option>
            {column.options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      }

      default: {
        return (
          <input
            ref={inputRef}
            type="text"
            value={value || ''}
            onChange={e => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full px-2 py-1 text-sm border border-brand-500 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
            disabled={isLoading}
          />
        );
      }
    }
  };

  return (
    <div className="relative flex items-center space-x-1 w-full">
      <div className="flex-1">{renderEditor()}</div>

      <div className="flex items-center space-x-1">
        <button
          type="button"
          onClick={handleSave}
          disabled={isLoading}
          className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded disabled:opacity-50"
          title="Save (Enter)"
        >
          <Check className="h-3 w-3" />
        </button>

        <button
          type="button"
          onClick={handleCancel}
          disabled={isLoading}
          className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded disabled:opacity-50"
          title="Cancel (Escape)"
        >
          <X className="h-3 w-3" />
        </button>
      </div>

      {/* Error Message */}
      {showError &&
        error &&
        createPortal(
          <div className="fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded shadow-lg z-50">
            <div className="flex items-center">
              <X className="h-4 w-4 mr-2" />
              <span className="text-sm">{error}</span>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};
